import React, { useCallback, useEffect, useState } from 'react';
import { useParams } from 'react-router-dom';
import EntityTable from '../../components/workspace/EntityTable';
import { useApi } from '../../lib/api';
import { useWorkspace } from '../../lib/workspace';

export default function Departments() {
  const { appId } = useParams();
  const api = useApi();
  const { canWrite } = useWorkspace();

  const [branches, setBranches] = useState([]);
  const [departments, setDepartments] = useState([]);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      const [brs, ds] = await Promise.all([
        api.branches.list(appId).catch(() => ({ items: [] })),
        api.departments.list(appId).catch(() => ({ items: [] })),
      ]);
      if (cancelled) return;
      setBranches(brs.items || []);
      setDepartments(ds.items || []);
    })();
    return () => { cancelled = true; };
  }, [api, appId]);

  const fetcher = useCallback(() => api.departments.list(appId), [api, appId]);

  const branchLookup = branches.map((b) => ({ value: String(b.id), label: b.name }));
  const deptLookup   = departments.map((d) => ({ value: String(d.id), label: d.name }));

  const COLUMNS = [
    { key: 'name', label: 'Name' },
    { key: 'code', label: 'Code' },
    {
      key: 'branch_id',
      label: 'Branch',
      render: (r) =>
        branches.find((b) => b.id === r.branch_id)?.name ||
        <span className="muted">—</span>,
    },
    {
      key: 'parent_department_id',
      label: 'Parent dept',
      render: (r) =>
        departments.find((d) => d.id === r.parent_department_id)?.name ||
        <span className="muted">—</span>,
    },
  ];

  const FIELDS = [
    { name: 'name',                 label: 'Name',        required: true, maxLen: 200, half: true },
    { name: 'code',                 label: 'Code',        maxLen: 50, half: true },
    { name: 'branch_id',            label: 'Branch',      type: 'lookup', lookupOptions: branchLookup, half: true },
    { name: 'parent_department_id', label: 'Parent dept', type: 'lookup', lookupOptions: deptLookup,   half: true },
    { name: 'description',          label: 'Description', type: 'textarea', rows: 3, maxLen: 2000 },
  ];

  return (
    <EntityTable
      title="Departments"
      fetcher={fetcher}
      columns={COLUMNS}
      formFields={FIELDS}
      onCreate={(body)      => api.departments.create(appId, body)}
      onUpdate={(id, body)  => api.departments.update(appId, id, body)}
      onDelete={(id)        => api.departments.remove(appId, id)}
      canWrite={canWrite}
      emptyMessage="No departments yet."
      onRowsLoaded={setDepartments}
    />
  );
}
